import React, { useState, useEffect } from 'react';
import { generatePassword } from '../../hooks/useSettings';
import type { DentalOfficeProvider } from '../../types';

interface ProviderFormProps {
  provider?: DentalOfficeProvider;
  onSubmit: (data: ProviderFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export interface ProviderFormData {
  name: string;
  email: string;
  password: string;
  role: 'dentist' | 'hygienist' | 'admin' | 'receptionist' | 'assistant';
  can_view_records: boolean;
  can_edit_records: boolean;
  can_schedule: boolean;
  can_billing: boolean;
  is_active: boolean;
}

const roles = [
  { value: 'dentist', label: 'Tannlæknir' },
  { value: 'hygienist', label: 'Tannfræðingur' },
  { value: 'admin', label: 'Stjórnandi' },
  { value: 'receptionist', label: 'Ritari' },
  { value: 'assistant', label: 'Aðstoðarmaður' },
];

export function ProviderForm({ provider, onSubmit, onCancel, isLoading }: ProviderFormProps) {
  const [formData, setFormData] = useState<ProviderFormData>({
    name: provider?.name || '',
    email: provider?.email || '',
    password: '',
    role: provider?.role || 'dentist',
    can_view_records: provider?.can_view_records ?? true,
    can_edit_records: provider?.can_edit_records ?? false,
    can_schedule: provider?.can_schedule ?? false,
    can_billing: provider?.can_billing ?? false,
    is_active: provider?.is_active ?? true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (provider) {
      setFormData({
        name: provider.name,
        email: provider.email ?? '',
        password: '',
        role: provider.role,
        can_view_records: provider.can_view_records,
        can_edit_records: provider.can_edit_records,
        can_schedule: provider.can_schedule,
        can_billing: provider.can_billing,
        is_active: provider.is_active,
      });
    }
  }, [provider]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleGeneratePassword = () => {
    const password = generatePassword();
    setFormData((prev) => ({ ...prev, password }));
    setShowPassword(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    // Validation
    if (!formData.name.trim()) {
      newErrors.name = 'Nafn er áskilið';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Netfang er áskilið';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ógilt netfang';
    }

    if (!provider && !formData.password) {
      newErrors.password = 'Lykilorð er áskilið fyrir nýjan starfsmann';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Nafn *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.name ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Netfang *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.email ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Lykilorð {!provider && '*'}
        </label>
        <div className="flex gap-2">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder={provider ? 'Skilja eftir autt til að halda núverandi' : ''}
            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.password ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          <button
            type="button"
            onClick={handleGeneratePassword}
            className="px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            Búa til
          </button>
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {showPassword ? 'Fela' : 'Sýna'}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1 text-sm text-red-600">{errors.password}</p>
        )}
      </div>

      {/* Role */}
      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
          Hlutverk *
        </label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {roles.map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </select>
      </div>

      {/* Permissions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Heimildir
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="can_view_records"
              checked={formData.can_view_records}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Skoða sjúkraskrár
            </span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              name="can_edit_records"
              checked={formData.can_edit_records}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Breyta sjúkraskrám
            </span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              name="can_schedule"
              checked={formData.can_schedule}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Tímabóka
            </span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              name="can_billing"
              checked={formData.can_billing}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Sjá og stjórna greiðslum
            </span>
          </label>
        </div>
      </div>

      {/* Active Status (for editing only) */}
      {provider && (
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Virkur starfsmaður
            </span>
          </label>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          disabled={isLoading}
        >
          Hætta við
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          disabled={isLoading}
        >
          {isLoading ? 'Vinnur...' : provider ? 'Uppfæra' : 'Stofna'}
        </button>
      </div>
    </form>
  );
}
